import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { Form, Col } from 'react-bootstrap'
import {
  ApiContent,
  checkEnableButton,
  currencyFormat,
  enablePdpRedesign,
  i18nTranslate,
  IS_BROWSER,
  replaceLocaleInURL,
} from 'src/utils'
import { productDeps } from 'src/deps'
import {
  customerContainer,
  mysiteContainer,
  nuskinSubscriptionContainer,
  storeContainer,
} from 'src/models'
import { isEquinoxMysite } from 'src/utils/mySiteUtils'
import { LearnMoreSubscriptionModal } from '../../SubscriptionComponents'
import { productState } from '../../BaseProductComponent'
import { PDPUnitPrice } from '../../Price'
import SubscriptionFrequency from './SubscriptionFrequency'
import './styles.scss'

@observer
class SubscribeAndSave extends Component {
  static contextType = ApiContent
  constructor(props) {
    super(props)
    this.state = {
      // If you want to manage checked state internally, add it here
      isChecked: this.props?.shouldSubscribeOnly || false,
      showLearnMoreSubsModal: false,
    }
  }

  componentDidUpdate(prevProps) {
    const prevProductId = this.props?.fromSubProduct
      ? prevProps?.subProduct?.identifier
      : prevProps?.product?.productId
    const currentProductId = this.props?.fromSubProduct
      ? this.props?.subProduct?.identifier
      : this.props?.product?.productId
    if (
      prevProductId &&
      currentProductId &&
      prevProductId !== currentProductId
    ) {
      this.setState({
        isChecked: this.props?.shouldSubscribeOnly || false,
        showLearnMoreSubsModal: false,
      })
      productState.showSubscriptionPriceLabel = false
    }
  }

  toggleLearnMoreSubsModal = () => {
    this.setState(prevState => ({
      showLearnMoreSubsModal: !prevState.showLearnMoreSubsModal,
    }))
  }

  getHelpLinkWithLocale = () => {
    const subsHelpLink =
      'https://www.nuskin.com/content/nuskin/<locale>/corporate/help/customer-service.html'
    const link = replaceLocaleInURL({ url: subsHelpLink })
    if (IS_BROWSER) {
      window.open(link)
    }
  }

  updateCheckboxState = newCheckedState => {
    this.setState({ isChecked: newCheckedState })
    if (!this.props?.fromSubProduct) {
      productState.currentlySelectedPriceOption = newCheckedState
        ? 'subscription-price-button'
        : 'one-time-price-button'
    }
    if (this.props.handleSubProductSubscribeSelect) {
      this.props.handleSubProductSubscribeSelect(true, newCheckedState)
    }
  }

  handleCheckboxChange = e => {
    this.updateCheckboxState(e.target.checked)
    productState.showSubscriptionPriceLabel = e.target.checked
  }

  getAccountType = () => {
    if (IS_BROWSER) {
      return customerContainer.profileResponse?.accounts?.type || ''
    }
    return this?.context?.customerType
  }

  getSubscriptionLabelText = savings => {
    const amount = savings ?? this.getSavings()
    return amount
      ? i18nTranslate('pdp.subscribeAndSave', 'Subscribe and Save', {
          nameSpace: 'ssr-resource',
        })
      : i18nTranslate('pdp.subscribe', 'Subscribe', {
          nameSpace: 'ssr-resource',
        })
  }

  isNegativeDiscount = unitValue =>
    unitValue?.priceAfterDiscount > unitValue?.originalPrice

  isBAorPreferred = () => {
    const customerType = this.getAccountType()
    const isAccountBAorPreferred =
      customerType === 'Brand Affiliate' ||
      customerType === 'Brand Affiliate - Business Entity' ||
      customerType === 'Preferred Customer/Member'
    // On mysite with member pricing, unregistered visitors should be treated
    // as BA/Preferred so subscription savings use the Sub Wholesale facet.
    if (!customerContainer?.isRegisterUser && isEquinoxMysite()) {
      return (
        isAccountBAorPreferred || mysiteContainer.mysitePriceType == 'member'
      )
    }
    return isAccountBAorPreferred || false
  }

  isBAorBE = () => {
    const customerType = this.getAccountType()
    return (
      customerType === 'Brand Affiliate' ||
      customerType === 'Brand Affiliate - Business Entity'
    )
  }

  isBundleOrKit =
    this.props.product?.type === 'bundle' || this.props.product?.type == 'kit'

  shouldShowSubscriptionBox = () => {
    const availableChannelsList = this.isBundleOrKit
      ? this.props.product?.properties?.availableChannels || ''
      : this.props.product?.availableChannels || ''

    const shouldShowSubscription = checkEnableButton(
      availableChannelsList,
      'subscription'
    )
    return shouldShowSubscription
  }

  getSavings = () => {
    const unitValue = productDeps.getUnitPriceFacetsValue(
      this.props,
      this.props?.fromSubProduct
    )
    const priceFacets = unitValue?.priceFacets || {}

    const subscriptionSavings = productDeps.getSubscriptionSavings(
      priceFacets,
      this.isBAorPreferred()
    )

    const subscriptionPromotion =
      this.props.product?.subscriptionPromotion || []
    if (
      Array.isArray(subscriptionPromotion) &&
      subscriptionPromotion.length > 0 &&
      subscriptionPromotion[0]?.discount > 0 &&
      subscriptionSavings > 0
    ) {
      return subscriptionPromotion[0]?.discount
    }

    return subscriptionSavings
  }

  formattedSavings = () => {
    const unitValue = productDeps.getUnitPriceFacetsValue(
      this.props,
      this.props?.fromSubProduct
    )
    let Savings = this.getSavings() || 0
    if (Savings && Savings > 0 && !this.isNegativeDiscount(unitValue)) {
      return currencyFormat({ price: Savings })
    }
  }
  renderSubscribeAndSaveText = savings => {
    const savingsAmount = savings ?? this.getSavings()
    return (
      <div
        className="subs-and-save-container ml-2"
        data-testid="qa-subs-save-text">
        <span className={'subs-save-text'}>
          {this.getSubscriptionLabelText(savingsAmount)}
          {savingsAmount > 0 && (
            <span className="subs-save-amount ml-1">
              {currencyFormat({ price: savingsAmount })}
            </span>
          )}
        </span>
        {savingsAmount > 0 && (
          <span className="after-discount-text ml-1">
            {i18nTranslate('on_this_item_text', 'on this item', {
              nameSpace: 'ssr-resource',
            })}
          </span>
        )}
      </div>
    )
  }

  renderBenefitsAndLearnMore = () => {
    const benefitText = this.isBAorBE()
      ? i18nTranslate(
          'subscriptions.subscriber_benefit_text_ba_be',
          'Subscribers enjoy free shipping & returns',
          { nameSpace: 'ssr-resource' }
        )
      : i18nTranslate(
          'subscriptions.subscriber_benefit_text',
          'Subscribers earn loyalty rewards, and enjoy free shipping & returns',
          { nameSpace: 'ssr-resource' }
        )

    return (
      <div className="paragraph-m">
        {benefitText}{' '}
        <button
          className="learn-more-div learn-more-link"
          data-testid="qa-pdp-learn-more-text"
          type="button"
          onClick={this.toggleLearnMoreSubsModal}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              this.toggleLearnMoreSubsModal()
            }
          }}
          aria-label={i18nTranslate(
            'subscriptions.learnMore_aria_label',
            'Learn more about subscription benefits',
            { nameSpace: 'ssr-resource' }
          )}
          aria-expanded={this.state.showLearnMoreSubsModal}>
          {i18nTranslate('pdp.paypallink', 'Learn More')}
        </button>
      </div>
    )
  }

  renderCheckBox = () => {
    const savings = this.getSavings()
    const checkboxLabel =
      savings > 0
        ? `${i18nTranslate('pdp.subscribeAndSave', 'Subscribe and Save', { nameSpace: 'ssr-resource' })} ${currencyFormat({ price: savings })} ${i18nTranslate('on_this_item_text', 'on this item', { nameSpace: 'ssr-resource' })}`
        : i18nTranslate('pdp.subscribe', 'Subscribe', {
            nameSpace: 'ssr-resource',
          })
    return (
      <div>
        <div className="subscription-check pl-0">
          <Form.Check
            type="checkbox"
            className="subscription-checkbox pl-0"
            label={this.renderSubscribeAndSaveText(savings)}
            id={`checkbox_${this.props?.fromSubProduct ? this.props?.subProduct?.identifier : this.props?.product?.productId}`}
            checked={this.state.isChecked}
            onChange={this.handleCheckboxChange}
            data-testid="qa-subscription-checkbox"
            custom={false}
            disabled={this.props?.shouldSubscribeOnly}
            aria-label={checkboxLabel}
            // Mirrors the native checked state; automation asserts on it.
            aria-checked={this.state.isChecked}
          />
        </div>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          data-testid="qa-subscription-checkbox-announcement">
          {this.state.isChecked
            ? i18nTranslate(
                'subscriptions.subscription_enabled',
                'Subscription enabled',
                { nameSpace: 'ssr-resource' }
              )
            : i18nTranslate(
                'subscriptions.subscription_disabled',
                'Subscription disabled',
                { nameSpace: 'ssr-resource' }
              )}
        </div>
      </div>
    )
  }

  render() {
    const { isChecked } = this.state
    const enableLoyaltyPoints = storeContainer.getLoyaltyPointsConfig() || false
    return (
      // this.getSavings() > 0 &&
      this.shouldShowSubscriptionBox() && (
        <div
          data-testid="qa-subscribeandsave-text"
          className={`subscribeAndSave-container ${enablePdpRedesign() ? 'new-pdp-design' : ''}`}>
          {this.renderCheckBox()}
          <div
            className="subs-item-info-container"
            data-testid="qa-subs-item-info">
            <div className="subscription-divider" aria-hidden="true"></div>
            <div className="price-container my-4" data-testid="qa-subs-price">
              <PDPUnitPrice
                {...this.props}
                product={
                  this.props.fromSubProduct
                    ? this.props.subProduct
                    : this.props.product
                }
                fromSubProduct={this.props.fromSubProduct}
                showSubscriptionPrice={true}
              />
            </div>
            {isChecked && !enableLoyaltyPoints && (
              <div className="subscription-frequency">
                <SubscriptionFrequency
                  isFromPDP={true}
                  isMonthlyLabel={true}
                  selectedSku={this.props?.subProduct?.selectedSku}
                  handleSubProductSubscribeSelect={
                    this.props?.handleSubProductSubscribeSelect
                  }
                />
              </div>
            )}
            {!this.props?.hideBenefits && (
              <div
                className="benefits-container"
                data-testid="qa-subs-benefit-info">
                {this.renderBenefitsAndLearnMore()}
              </div>
            )}
          </div>
          <LearnMoreSubscriptionModal
            show={this.state.showLearnMoreSubsModal}
            onHide={this.toggleLearnMoreSubsModal}
            subsFAQ={this.getHelpLinkWithLocale}
          />
        </div>
      )
    )
  }
}

export { SubscribeAndSave }
export default SubscribeAndSave
